import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import { empRoute, employer, publicUser } from "../../../../../globals/route-names";
import { jobService } from "../../../../../services/job-service";

const JOB_TYPES = ["Freelance", "Full Time", "Internship", "Part Time", "Temporary", "Volunteer"];

function EmpPostAJobPage() {
    const navigate = useNavigate();
    const { id: editJobId } = useParams();
    const { user } = useAuth();
    const isEditing = Boolean(editJobId);
    const [message, setMessage] = useState("");
    const [companies, setCompanies] = useState([]);
    const [companySearch, setCompanySearch] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [jobImageFile, setJobImageFile] = useState(null);
    const [posterFile, setPosterFile] = useState(null);
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({
        title: "",
        company_id: "",
        category: "",
        subcategory: "",
        job_type: "Full Time",
        salary_min: "",
        salary_max: "",
        experience: "",
        qualification: "",
        location: "",
        workplace_type: "On-site",
        description: "",
        expires_at: "",
        support_email: user?.email || "",
        support_phone: user?.phone || "",
        mask_contact_details: false
    });

    useEffect(() => {
        jobService.jobCategories()
            .then(data => {
                const cats = data.categories || [];
                setCategories(cats);
                if (!isEditing && cats.length > 0) {
                    const firstMain = cats.find(c => !c.parent_id);
                    if (firstMain) {
                        setForm(f => ({ ...f, category: firstMain.name }));
                    }
                }
            })
            .catch(err => console.error(err));
    }, [isEditing]);

    useEffect(() => {
        jobService.companies({ keyword: companySearch })
            .then((data) => {
                const list = data.companies || [];
                setCompanies(list);
                if (!form.company_id && !companySearch && list.length > 0) {
                    const ownPending = list.find((company) => !company.verified && company.owner_user_id);
                    const preferred = ownPending || list[0];
                    setForm((current) => current.company_id ? current : { ...current, company_id: preferred.id });
                }
            })
            .catch((error) => setMessage(error.message));
    }, [companySearch, form.company_id]);

    useEffect(() => {
        if (!editJobId) return;
        setMessage("");
        jobService.employerJobs()
            .then((data) => {
                const job = (data.jobs || []).find((item) => item.id === editJobId);
                if (!job) {
                    setMessage("Job not found or you do not have permission to edit it.");
                    return;
                }
                setForm({
                    title: job.title || "",
                    company_id: job.company_id || "",
                    category: job.category || "",
                    subcategory: job.subcategory || "",
                    job_type: job.job_type || "Full Time",
                    salary_min: job.salary_min || "",
                    salary_max: job.salary_max || "",
                    experience: job.experience || "",
                    qualification: job.qualification || "",
                    location: job.location || "",
                    workplace_type: job.workplace_type || "On-site",
                    description: job.description || "",
                    expires_at: job.expires_at || "",
                    support_email: job.support_email || user?.email || "",
                    support_phone: job.support_phone || user?.phone || "",
                    mask_contact_details: Boolean(job.mask_contact_details)
                });
            })
            .catch((error) => setMessage(error.message));
    }, [editJobId, user?.email, user?.phone]);

    const selectedCompany = useMemo(() => companies.find((company) => company.id === form.company_id), [companies, form.company_id]);

    const update = (event) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setForm({ ...form, [event.target.name]: value });
    };

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setMessage("");
        try {
            const data = isEditing
                ? await jobService.updateEmployerJob(editJobId, form)
                : await jobService.createEmployerJob(form);
            const jobId = data.id || editJobId;
            if (jobImageFile) await jobService.uploadJobImage(jobId, jobImageFile);
            if (posterFile) await jobService.uploadJobPoster(jobId, posterFile);
            navigate(empRoute(employer.MANAGE_JOBS));
        } catch (error) {
            setMessage(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="wt-admin-right-page-header clearfix">
                <h2>{isEditing ? "Edit Job" : "Post a Job"}</h2>
                <div className="breadcrumbs"><NavLink to={publicUser.HOME1}>Home</NavLink><NavLink to={empRoute(employer.DASHBOARD)}>Dashboard</NavLink><span>{isEditing ? "Edit Job Details" : "Job Submission Form"}</span></div>
            </div>

            <div className="panel panel-default">
                <div className="panel-heading wt-panel-heading p-a20">
                    <h4 className="panel-tittle m-a0"><i className="fa fa-suitcase" />Job Details</h4>
                </div>
                <div className="panel-body wt-panel-body p-a20 m-b30 ">
                    {message && <div className="alert alert-danger">{message}</div>}
                    <form onSubmit={submit}>
                        <div className="row">
                            <div className="col-md-12">
                                <div className="alert alert-info">
                                    {isEditing
                                        ? "Edit the job details and submit again. The job will move to admin review and will be published only after admin approval."
                                        : "Search and select an approved company. If you recently added a new company, it is selected here and you can send the job for admin review even before company approval."}
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Search Company</label>
                                    <input className="form-control" type="text" value={companySearch} onChange={(event) => setCompanySearch(event.target.value)} placeholder="Type company name or location" />
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Select Company</label>
                                    <select className="form-control" name="company_id" required value={form.company_id} onChange={update}>
                                        <option value="">Choose company</option>
                                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name} {company.location ? `- ${company.location}` : ""} {!company.verified ? "(Pending admin approval)" : ""}</option>)}
                                    </select>
                                </div>
                            </div>
                            {selectedCompany && <div className="col-md-12"><div className={`alert ${selectedCompany.verified ? "alert-success" : "alert-warning"}`}>Posting under: <strong>{selectedCompany.name}</strong>. {selectedCompany.verified ? "This approved company can be used by all employers." : "This company is not approved yet. Admin will see this clearly and can approve the company together with the job."}</div></div>}
                            <Field label="Job Title" name="title" value={form.title} onChange={update} required />
                            <div className="col-xl-4 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Job Category</label>
                                    <div className="ls-inputicon-box">
                                        <select 
                                            className="form-control" 
                                            name="category" 
                                            value={form.category} 
                                            onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
                                        >
                                            <option value="">Select Category</option>
                                            {categories.filter(c => !c.parent_id).map(c => (
                                                <option key={c.id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                        <i className="fs-input-icon fa fa-border-all" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-4 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Job Subcategory</label>
                                    <div className="ls-inputicon-box">
                                        <select 
                                            className="form-control" 
                                            name="subcategory" 
                                            value={form.subcategory} 
                                            onChange={update}
                                        >
                                            <option value="">Select Subcategory</option>
                                            {categories.filter(c => c.parent_id && categories.find(p => p.id === c.parent_id)?.name === form.category).map(c => (
                                                <option key={c.id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                        <i className="fs-input-icon fa fa-list" />
                                    </div>
                                </div>
                            </div>
                            <Select label="Job Type" name="job_type" value={form.job_type} onChange={update} options={JOB_TYPES} />
                            <Field label="Salary Min (INR)" name="salary_min" type="number" value={form.salary_min} onChange={update} />
                            <Field label="Salary Max (INR)" name="salary_max" type="number" value={form.salary_max} onChange={update} />
                            <Field label="Experience" name="experience" value={form.experience} onChange={update} placeholder="E.g. Minimum 3 years" />
                            <Field label="Qualification" name="qualification" value={form.qualification} onChange={update} placeholder="Qualification title" />
                            <Select label="Workplace Type" name="workplace_type" value={form.workplace_type} onChange={update} options={["On-site", "Hybrid", "Remote"]} />
                            <Field label="Location" name="location" value={form.location} onChange={update} required placeholder="City, Country" />
                            <Field label="Application End Date" name="expires_at" type="date" value={form.expires_at} onChange={update} />
                            <Field label="Support Email" name="support_email" type="email" value={form.support_email} onChange={update} placeholder="support@example.com" />
                            <Field label="Support Phone Number" name="support_phone" value={form.support_phone} onChange={update} placeholder="Phone number for this job" />
                            <div className="col-xl-4 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label className="d-block">Contact Privacy</label>
                                    <label><input type="checkbox" name="mask_contact_details" checked={form.mask_contact_details} onChange={update} /> Mask public contact details with xxx</label>
                                    <small className="d-block text-muted">Employer and job support contacts will be partially hidden on public job pages.</small>
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Job Image</label>
                                    <input className="form-control" type="file" accept="image/*" onChange={(event) => setJobImageFile(event.target.files?.[0] || null)} />
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-md-12">
                                <div className="form-group">
                                    <label>Job Top Poster</label>
                                    <input className="form-control" type="file" accept="image/*" onChange={(event) => setPosterFile(event.target.files?.[0] || null)} />
                                </div>
                            </div>
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea className="form-control" name="description" rows={5} required value={form.description} onChange={update} placeholder="Describe responsibilities, requirements, and benefits." />
                                </div>
                            </div>
                            <div className="col-lg-12 col-md-12">
                                <div className="text-left">
                                    <button type="submit" disabled={submitting} className="site-button m-r5">
                                        {submitting ? "Saving..." : isEditing ? "Save Changes for Admin Approval" : "Publish Job"}
                                    </button>
                                    {isEditing && <NavLink to={empRoute(employer.MANAGE_JOBS)} className="site-button-secondry m-l10">Cancel</NavLink>}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

function Field({ label, name, value, onChange, type = "text", required = false, placeholder = "" }) {
    return (
        <div className="col-xl-4 col-lg-6 col-md-12">
            <div className="form-group">
                <label>{label}</label>
                <div className="ls-inputicon-box">
                    <input className="form-control" name={name} type={type} required={required} placeholder={placeholder || label} value={value} onChange={onChange} />
                    <i className="fs-input-icon fa fa-address-card" />
                </div>
            </div>
        </div>
    );
}

function Select({ label, name, value, onChange, options }) {
    return (
        <div className="col-xl-4 col-lg-6 col-md-12">
            <div className="form-group">
                <label>{label}</label>
                <div className="ls-inputicon-box">
                    <select className="form-control" name={name} value={value} onChange={onChange}>
                        {options.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <i className="fs-input-icon fa fa-border-all" />
                </div>
            </div>
        </div>
    );
}

export default EmpPostAJobPage;
